import {
    convertLocalToUtcString,
    convertUtcToLocalString,
} from '../utils/timezone';
import {
    Checkboxes,
    CmsUserHistory,
    NullableTranslationMap,
    TranslationMap,
} from './index';

export type NhlGameStage = 'regular_season' | 'playoff';
export type NhlGameStatus = 'active' | 'inactive';

export interface KpiPromotions {
    activations: number;
    grants: number;
}
export interface KpiTeams {
    id: number;
    city: string;
    name: string;
    logo: string;
    count: number;
}

export interface KpiFirebase {
    eventCount: number;
    totalUsers: number;
}

export interface NhlGame {
    id: number;
    stage: NhlGameStage;
    status: NhlGameStatus;
    year: number;
    linkedGameId: number | null;
    timezone: string;
    startDate: string;
    endDate: string;
    endGameDate: string;
    isFeatured: boolean;
    title: TranslationMap;
    description: TranslationMap;
    image: NullableTranslationMap;
    detailImage: NullableTranslationMap;
    victoryNotificationTitle: TranslationMap;
    victoryNotificationBody: TranslationMap;
    subTitle: TranslationMap;
    buttonTitle: TranslationMap;
    checkboxes: Checkboxes[];
    marketingDeepLink: string | null;
    cmsUserHistory: CmsUserHistory[] | null;
    cmsUserEmail: string | null;
    lastPinsUploadDate: string;
    pendingPeriodPins: number | null;
    grandPrizeEndDate: string;
    rewards: NhlGameReward[];
    provinces: BusinessUnitProvince[];
}

export type BusinessUnitProvince = {
    id: number;
    province: Province;
};

export type Province = {
    id: number;
    name: string;
    ageNicotine: number | null;
};

export interface UpsertNhlGamePayload {
    stage: NhlGameStage;
    status: NhlGameStatus;
    year: number;
    timezone: string;
    startDate: string;
    endDate: string;
    endGameDate: string | null;
    linkedGameId: number | null;
    isFeatured?: boolean;
    title: TranslationMap;
    description: TranslationMap;
    image: NullableTranslationMap;
    detailImage: NullableTranslationMap;
    subTitle: TranslationMap;
    victoryNotificationTitle: TranslationMap;
    victoryNotificationBody: TranslationMap;
    buttonTitle: TranslationMap;
    checkboxes: Checkboxes[];
    marketingDeepLink: string | null;
    cmsUserEmail: string | null;
    cmsUserHistory: CmsUserHistory[] | null;
    grandPrizeEndDate: string | null;
    rewards: NhlGameReward[];
    provinces: GameFormProvince[];
}

export interface NhlGameForm {
    stage: NhlGameStage;
    status: NhlGameStatus;
    year: number | null;
    linkedGameId: number | null;
    timezone: string;
    startDate: string | null;
    endDate: string | null;
    endGameDate: string | null;
    isFeatured: boolean;
    title: TranslationMap;
    description: TranslationMap;
    image: NullableTranslationMap;
    detailImage: NullableTranslationMap;
    victoryNotificationTitle: TranslationMap;
    victoryNotificationBody: TranslationMap;
    subTitle: TranslationMap;
    buttonTitle: TranslationMap;
    checkboxes: Checkboxes[];
    marketingDeepLink: string | null;
    cmsUserEmail: string | null;
    cmsUserHistory: CmsUserHistory[] | null;
    grandPrizeEndDate: string | null;
    rewards: NhlGameReward[];
    provinces: GameFormProvince[];
}

export type GameFormProvince = {
    businessUnitId: number | null; // null for new selections
    provinceId: number;
};

export interface NhlGameKpi {
    newParticipations: number;
    newClients: number;
    kpiPromotions: KpiPromotions;
    teamsSelected: number;
    mostSelectedTeams: KpiTeams[];
    firebaseMetrics: KpiFirebase;
}

export type NhlGameKpiPeriod = {
    startDate: string;
    endDate: string;
};

export type NhlTeam = {
    id: number;
    city: string;
    name: string;
    logo: string;
    count?: number;
};

export interface NhlGamePinsKpi {
    ctPinsTotal: number;
    tbPinPendingValidation: number;
    tbPinsNeverValidated: number;
    tbPinsTotal: number;
    tbPinsUsed: number;
    tbPinsValidated: number;
    tbPredsNoPin: number;
    tbPredsWithPin: number;
    totalInscriptions: number;
    tbPredsWithFreePin: number;
    tbPinsTotalFree: number;
    tbPinsTotalInputted: number;
    rewardsWon: number;
    rewardsActivated: number;
    mostPopularTeam: NhlTeam;
}

export interface NhlGameReward {
    id: number | null;
    active: boolean;
    promotionId: number;
    pucksValue: number;
}

export interface NhlGameStoresPinsKpi {
    nref: string;
    street: string;
    postalCode: string;
    city: string;
    validatedTbPinCount: number | null;
}

export interface NhlGameAddPinsRequest {
    pins: PinData[];
}

export type PinData = {
    pin: string;
    storeNref: string;
};

export interface NhlGameAddPinsResponse {
    pinsValidated: number;
    ctPinsEntered: number;
    invalidPins: number;
}

export interface NhlGameKpiCsv {
    userId: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    language: string;
    createdAt: string;
}

export interface Leaderboard {
    userId: number;
    firstName: string;
    lastName: string;
    email: string;
    totalPoints: number | null;
    createdAt: string;
}

export interface NhlGameLeaderboard {
    page: number;
    perPage: number;
    total: number;
    result: Leaderboard[];
}

export const convertFormToNhlGamePayload = (
    gameForm: NhlGameForm
): UpsertNhlGamePayload => {
    const { isFeatured: _isFeatured, ...rest } = gameForm;
    return {
        ...rest,
        // Here, dates are moment objects.
        startDate:
            convertLocalToUtcString(gameForm.startDate, gameForm.timezone) ||
            '',
        endDate:
            convertLocalToUtcString(gameForm.endDate, gameForm.timezone) || '',
        endGameDate:
            convertLocalToUtcString(gameForm.endGameDate, gameForm.timezone) ||
            '',
        grandPrizeEndDate:
            convertLocalToUtcString(
                gameForm.grandPrizeEndDate,
                gameForm.timezone
            ) || '',
        year: gameForm.year || 0,
    };
};

export const convertNhlGameToForm = (game: NhlGame): NhlGameForm => {
    return {
        ...game,
        isFeatured: true,
        // Here, dates are coming in as UTC strings from the API.
        startDate: convertUtcToLocalString(game.startDate, game.timezone),
        endDate: convertUtcToLocalString(game.endDate, game.timezone),
        endGameDate: convertUtcToLocalString(game.endGameDate, game.timezone),
        grandPrizeEndDate: convertUtcToLocalString(
            game.grandPrizeEndDate,
            game.timezone
        ),
        provinces: game.provinces.map((p) => ({
            businessUnitId: p.id,
            provinceId: p.province.id,
        })),
    };
};
